import React, { useEffect, useRef, useState } from "react"
import { createStyles } from "@mantine/core"
import { MantineReactTable, useMantineReactTable } from "mantine-react-table"
import { renderToolbarInternalActions } from "app/static/frontend/sheets/components/ToolbarInternalActions"

import { getSortedRowModel } from "app/static/frontend/sheets/mantine-sheet/getSortedRowModel"
import {
    isColumnActivelyFiltered,
    getSafedSearchRowId,
} from "app/static/frontend/sheets/mantine-sheet/mantine-sheet-helper-functions"
import sheetConstants from "app/static/frontend/sheets/constants"
import { filterFns } from "app/static/frontend/sheets/mantine-sheet/filterFunctions"

// We're overriding the built-in Mantine React Table styles, and that requires winning a specificity competition.
// When you see multiple &s, we're chaining the class name to increase specificity and beat out the MRT styles.
// @ts-expect-error
const useStyles: (x: any) => any = createStyles((theme, { groupingColumns }) => ({
    cell: {
        "& *": { fontSize: "16px" },
        "&&": {
            // This height has no effect, except to make height: 100% work on its children
            // however, when virtualization is enabled, this truncates all rows to 1px permanently
            // due to the flexbox -> grid css computation with virtualization on
            height: undefined,
            padding: 0,
            justifyContent: "center",
            alignItems: "stretch",

            // Re-enable overflow: visible, so that Select dropdowns are visible.
            // Note this means we need to handle text overflow ourselves (Using CellWrapper).
            overflow: "visible",

            ".datum-link > div": {
                padding: 0,
            },
        },
    },
    datumCell: {
        "&&": {
            justifyContent: "left",
        },
    },
    row: {
        // Similarly, we can isolate all the styles in `<td />`.
        "&&": {
            "&:hover td": {
                // Highlight the Index Cell
                ":first-child": {
                    color: `${theme.colors.violet[9]}`,
                    backgroundColor: `${theme.colors.violet[3]}`,
                },

                // Links should be Violet.9 and underlined
                "& a": {
                    color: `${theme.colors.violet[9]}`,
                    textDecoration: "underline",
                },
            },
            // This styles the row-number cells
            "td:first-child": {
                color: theme.colors.gray[7],
                textAlign: "center",
                alignItems: "center",
            },
            // make the bottom border visible in virtualized state by bringing the row to front
            // this is not an ideal solution but we have slightly less control over the rendering process
            // with virtualization...
            "&:hover": {
                zIndex: 2,
            },
        },
    },
    // base header styles
    header: {
        "& *": { fontSize: "14px" },
        "&&": {
            backgroundColor: theme.colors.gray[0],
            padding: ".5rem",
            ".header-text": {
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                overflow: "hidden",
                fontWeight: "500",
                paddingRight: "4px",
            },
            "&:not(:hover) button.mantine-ActionIcon-root:has(svg.tabler-icon-grip-horizontal)": {
                display: "none",
            },
            // this styles the divider that is used to resize the columns
            ".mantine-TableHeadCell-Content > div:nth-child(3)": {
                opacity: 0,
                zIndex: 10,
                margin: 0,
                padding: 0,
                right: "-10px",
            },
            // slider styles
            ".mantine-Slider-trackContainer": {
                zIndex: 0,
            },
            // general input styles
            ".mantine-InputWrapper-root": {
                minWidth: 0,
            },
            ".mantine-Input-input, .mantine-MultiSelect-values > input, span": {
                fontWeight: 400,
            },
            ".mantine-Input-input": {
                border: "none",
            },
            ".mantine-InputWrapper-root:has(.mantine-Input-input:hover)": {
                backgroundColor: theme.colors.gray[1],
                borderColor: theme.colors.violet[7],
            },
            ".mantine-InputWrapper-root:has(.mantine-Input-input:focus)": {
                backgroundColor: theme.colors.gray[1],
                borderColor: theme.colors.violet[7],
            },
            ".mantine-InputWrapper-root:has(.mantine-Input-input:active)": {
                backgroundColor: theme.colors.gray[1],
                borderColor: theme.colors.violet[7],
            },
            // text input styles
            ".mantine-TextInput-input": {
                paddingLeft: "8px",
            },
            ".mantine-TextInput-input:focus": {
                border: "none",
                boxShadow: "none",
            },
            ".mantine-DateInput-input": {
                boxShadow: "none",
                paddingLeft: "8px",
            },
            // multi select styles
            ".mantine-InputWrapper-root:has(.mantine-MultiSelect-searchInput:hover)": {
                backgroundColor: theme.colors.gray[1],
                borderColor: theme.colors.violet[7],
            },
            ".mantine-InputWrapper-root:has(.mantine-MultiSelect-searchInput:focus)": {
                backgroundColor: theme.colors.gray[1],
                borderColor: theme.colors.violet[7],
            },
            ".mantine-InputWrapper-root:has(.mantine-MultiSelect-searchInput:active)": {
                backgroundColor: theme.colors.gray[1],
                borderColor: theme.colors.violet[7],
            },
            ".mantine-MultiSelect-values": {
                marginRight: 0,
            },
            ".mantine-MultiSelect-value": {
                backgroundColor: theme.colors.gray[3],
            },
            ".mantine-MultiSelect-input": {
                overflow: "hidden",
            },
            ".mantine-MultiSelect-wrapper[aria-expanded=“true”] .mantine-MultiSelect-values": {
                // use a super large number to allow any height
                maxHeight: "10000px",
            },
            ".mantine-MultiSelect-wrapper[aria-expanded=false] .mantine-MultiSelect-values": {
                height: "40px",
            },
            ".mantine-MultiSelect-searchInput": {
                minHeight: "40px",
            },
            // adjusting checkbox filter
            "& div:has(.mantine-Checkbox-root)": {
                justifyContent: "center",
            },
            ".mantine-Checkbox-labelWrapper": {
                display: "none",
            },
        },
    },
    // partial header styles applied on hover / filter / sort state change
    headerSmallDisplay: {
        "&&": {
            "& span:has(svg.tabler-icon-filter)": {
                display: "none",
            },
            "& div.mantine-Indicator-root:has(svg.tabler-icon-arrows-sort)": {
                display: "none",
            },
            "& div.mantine-Indicator-root:has(svg.tabler-icon-sort-ascending)": {
                display: "none",
            },
            "& div.mantine-Indicator-root:has(svg.tabler-icon-sort-descending)": {
                display: "none",
            },
        },
    },
    headerHideFilterHover: {
        "&&": {
            // using opacity over display: none at the top-level for a few reasons:
            // 1) the filter popover needs a dom element to attach itself to
            //    so it doesn't appear in the top left corner. we preserve the 'button'
            //    component and hide the svg inside to get this result
            // 2) we *can* do this because filter comes after the sort icon, and sort
            //    does not have (1) to deal with
            // 3) display: none is find on the svg to hide the visual artifact]
            "&:not(:hover) span svg.tabler-icon-filter": {
                opacity: 0,
                display: "none",
            },
        },
    },
    headerHideSortHover: {
        "&&": {
            "&:not(:hover) div.mantine-Indicator-root svg.tabler-icon-arrows-sort": {
                display: "none",
            },
            "&:not(:hover) div.mantine-Indicator-root svg.tabler-icon-sort-ascending": {
                display: "none",
            },
            "&:not(:hover) div.mantine-Indicator-root svg.tabler-icon-sort-descending": {
                display: "none",
            },
            "&:not(:hover) button.mantine-ActionIcon-root svg.tabler-icon-grip-horizontal": {
                display: "none",
            },
        },
    },
    // These two classes togather make the table fill the entire page, even if there's no rows
    container: { flex: 1 },
    paper: {
        "& *": { fontSize: "16px" },
        table: {
            borderRight: `1px solid ${theme.colors.gray[4]}`,
            borderCollapse: "separate",
            borderSpacing: 0,
        },
        // ACTION BUTTONS
        "div:has(.sheet-button-container)": {
            paddingLeft: 0,
            paddingRight: 0,
        },
        ".loading-div i": {
            marginRight: "5px",
        },
        // GROUPED DROPDOWN ARROW CELL/HEADER STYLES
        'th:has(button[aria-label="Expand all"])': {
            width: `${60 + groupingColumns.length * 18}px`,
        },
        'td:has(button[aria-label="Expand"])': {
            width: `${60 + groupingColumns.length * 18}px`,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
        },
        // CELLS BORDER
        "table > tbody": {
            "tr > td": {
                border: `1px solid ${theme.colors.gray[4]}`,
                borderLeft: `none`,
                borderBottom: `none`,
            },
            "tr > td:first-child": {
                borderLeft: `1px solid ${theme.colors.gray[4]}`,
            },

            // STRIPED ROWS
            "tr:nth-child(even) > td": {
                backgroundColor: `${theme.colors.gray[1]}`,
            },

            // CELL HOVER STYLES
            "tr:hover td": {
                backgroundColor: `${theme.colors.violet[1]}`,
                border: `1px double ${theme.colors.violet[7]}`,
                borderLeft: `none`,
            },
            "tr:first-of-type:hover > td, tr:first-of-type:hover td .colored-cell": {
                boxShadow: `0 1px 0 ${theme.colors.violet[7]} inset`,
                borderTop: "none",
            },
            "tr:hover td:first-child": {
                borderLeft: `1px double ${theme.colors.violet[7]}`,
            },

            // INTERACTIVE CELL STYLES
            "tr > td:has(a):hover": {
                backgroundColor: `${theme.colors.violet[3]}`,
            },
            "tr > td:has(div[data-interactive='true']):hover": {
                cursor: "pointer",
                backgroundColor: `${theme.colors.violet[3]}`,
            },
            "tr > td:has(div[data-interactive='true'] div:empty)": {
                backgroundColor: `${theme.colors.violet[1]}`,
            },
            // STRIPED ROWS
            "tr:nth-child(even) > td:has(div[data-interactive='true'] div:empty)": {
                backgroundColor: `${theme.colors.violet[2]}`,
            },
        },
        // HEADER BORDER
        "table > thead > tr": {
            th: {
                border: `1px solid ${theme.colors.gray[4]}`,
                borderLeft: `none`,
                // fixes a doubly-grouped table from having a blank spot
                // while also preserving the ::after behavior below
                "nth-child(n + 3)": {
                    position: "relative",
                },
            },
            "th:hover::after": {
                content: '""',
                position: "absolute",
                top: 0,
                right: 0,
                width: "3px",
                backgroundColor: theme.colors.violet[7],
                height: "100%",
                cursor: "col-resize",
            },
            "th:first-child": {
                borderLeft: `1px solid ${theme.colors.gray[4]}`,
            },
        },
        // FOOTER BORDER AND POSITION
        "table > tfoot > tr": {
            th: {
                border: `1px solid ${theme.colors.gray[4]}`,
                borderLeft: `none`,
            },
            "th:first-child": {
                borderLeft: `1px solid ${theme.colors.gray[4]}`,
            },
            "th:last-child": {
                borderRight: `none`,
            },
        },
        height: "100%",
        display: "flex",
        flexDirection: "column",
        border: "0 !important",

        ".sheet-cell > div:first-of-type": {
            maxWidth: "100%",
        },

        ".clicked-cell": {
            position: "relative",
        },
        ".customTagDropdown": {
            position: "absolute",
            width: "100%",
            top: "0",
            left: "0",
            padding: "4px 8px",
        },

        // This is to restrict the height of the new third party select component to the height of it's parent
        ".new-select-dropdown-cell > div:first-of-type": {
            height: "1px !important",
        },

        // Styling the Alert Banner lifted from the mantine source code
        ".newAlertBanner": {
            "& *": { fontSize: "14px" },
            "& .mantine-Alert-root": {
                display: "flex",
                backgroundColor: "white",
                height: "24px",
                border: `none`,
                overflow: "visible",
                paddingLeft: "0px",

                // All texts that are not badges should have the color Gray.7
                "& div": {
                    color: `${theme.colors.gray[7]}`,
                    display: "flex",
                    alignItems: "center",
                },

                // Styling for the badge div
                ".mantine-Badge-root": {
                    backgroundColor: `${theme.colors.violet[1]}`,
                    color: `${theme.colors.violet[8]}`,
                    border: `1px solid ${theme.colors.violet[2]}`,
                    borderRadius: "20px",
                    padding: "0px 10px",
                    marginTop: "-2px",
                },

                // Styling for the badge content
                ".mantine-Badge-inner": {
                    textTransform: "capitalize",
                },

                // Styling for the cross icon within a badge
                ".mantine-Badge-rightSection": {
                    marginLeft: "4px",

                    "& button": {
                        color: `${theme.colors.violet[8]}`,
                    },
                },
            },
        },
    },
    footer: {
        "& *": { fontSize: "14px" },
        "&& th": {
            fontWeight: "500",
            textAlign: "right",
            padding: "8px 16px",
            height: "40px",
        },
    },
    // Hide the in-built Alert Banner
    alertBanner: {
        "&&": {
            display: "none",
        },
    },
}))

const ROW_NUMBERS_DISPLAY_COLUMN_ID = sheetConstants.REMOVE_ROW_COLUMN_INDEX
const ROW_EXPAND_DISPLAY_COLUMN_ID = "mrt-row-expand"

const aggregationFns = {
    numberSum: (columnId, _leafRows, childRows) => {
        return childRows.reduce((sum, next) => {
            const nextValue = next.getValue(columnId)
            const numericValue = Number(nextValue)

            return !isNaN(numericValue) ? sum + numericValue : sum
        }, 0)
    },
}

const setPlumId = (elements, plumId) => {
    elements.forEach((element) => {
        const parentElement = element.parentElement
        if (parentElement instanceof HTMLButtonElement) {
            parentElement.setAttribute("data-plum-id", plumId)
        }
    })
}

const getColumnCanGlobalFilter = () => true

export const MantineReactTableWrapper = ({
    id,
    columns,
    data,
    enableColumnOrdering,
    enableColumnDragging,
    enableColumnResizing,
    enableDensityToggle,
    enableRowNumbers,
    enableFullScreen,
    enableStickyHeader,
    enableStickyFooter,
    onColumnSizingChange,
    onColumnOrderChange,
    onColumnFiltersChange,
    onSortingChange,
    enableGrouping,
    renderTopToolbar,
    renderTopToolbarCustomActions,
    rowCount,
    isLoading,
    tableContainerRef,
    canFitInMemory,
    enableGlobalFilter,
}: {
    id?: number | string
    columns?: any[]
    data?: any
    enableColumnOrdering?: boolean
    enableColumnDragging?: boolean
    enableColumnResizing?: boolean
    enableDensityToggle?: boolean
    enableRowNumbers?: boolean
    enableFullScreen?: boolean
    enableStickyHeader?: boolean
    enableStickyFooter?: boolean
    onColumnSizingChange?: (...args: any[]) => unknown
    onColumnOrderChange?: (...args: any[]) => unknown
    onColumnFiltersChange?: (...args: any[]) => unknown
    onSortingChange?: (...args: any[]) => unknown
    enableGrouping?: boolean
    renderTopToolbar?: any
    renderTopToolbarCustomActions?: any
    rowCount?: number
    isLoading?: boolean
    tableContainerRef?: any
    canFitInMemory?: boolean
    enableGlobalFilter?: boolean
}) => {
    const columnsRef = useRef(columns)

    // Update the ref whenever columns changes
    useEffect(() => {
        columnsRef.current = columns
    }, [columns])

    const globalFilterFn = (rowModel, columnId, value) => {
        // overall search heuristic:
        // 1) look to a search value function in the column meta
        // 2) if it exists, compare and **respect the result as final**
        // 3) if it does not exist, compare against the raw data
        // mixing and matching raw data vs searching function will
        // result in unintended results showing up in case there is
        // an accidental overlap in numeric values (e.g. enums) and
        // representational labels

        const columns = columnsRef.current

        if (!rowModel || !columnId) {
            return true
        }
        if (columnId === sheetConstants.DATUM_COLUMN_INDEX.toString()) {
            return rowModel
                ?.getValue(columnId)
                ?.toString()
                ?.trim()
                ?.toLowerCase()
                ?.includes(value?.trim()?.toLowerCase())
        }

        const columnModel = columns.find((c) => c?.id?.toString() === columnId?.toString())
        if (columnModel?.meta?.searchStringGenerator) {
            const generator = columnModel.meta.searchStringGenerator
            const searchString = generator(rowModel)

            return searchString?.toString()?.trim()?.toLowerCase()?.includes(value?.trim()?.toLowerCase())
        }

        const rowId = getSafedSearchRowId(rowModel)
        const originalColumn = columns.find((c) => c?.id?.toString() === id?.toString())
        const originalValue = originalColumn?.data?.[rowId]
        if (originalValue?.toString()?.trim()?.toLowerCase()?.includes(value?.trim()?.toLowerCase())) {
            return true
        }

        // defer to any edge case in searching here. Please try to fix cases before this point if possible so
        // this remains Quorum-agnostic
        return false
    }

    const handleColumnSizeChange = (data) => {
        const [columnId, newWidth] = Object.entries(data())[0]
        return onColumnSizingChange(columnId, newWidth)
    }

    /* When mananging the column order state, we must take into account the automatically
        generated "display columns" based on our desired table behavior and MRT's internal
        display column generation. See https://www.mantine-react-table.com/docs/guides/display-columns
        for more information.
    */
    const [columnOrder, setColumnOrder] = useState([
        ...(enableRowNumbers ? [ROW_NUMBERS_DISPLAY_COLUMN_ID] : []),
        ...columns.map((column) => String(column.id)),
    ])
    const [columnPinning, setColumnPinning] = useState<any>({
        left: [
            ...(enableRowNumbers ? [ROW_NUMBERS_DISPLAY_COLUMN_ID] : []),
            ...columns.filter((column) => column.pinned).map((column) => String(column.id)),
        ],
        right: [],
    })
    const [groupingColumns, setGroupingColumns] = useState([])
    const [columnVisibility, setColumnVisibility] = useState({
        [ROW_NUMBERS_DISPLAY_COLUMN_ID]: true,
    })

    const { classes, cx } = useStyles({
        groupingColumns,
    })
    const columnOrderToUse = useRef(null)

    // payload here can be either the direct state OR an updater function
    // useState setters can handle the updater function fine but we need
    // to handle it ourselves too if it comes up.
    const handleColumnOrderChange = (payload) => {
        columnOrderToUse.current = payload

        if (typeof payload === "function") {
            columnOrderToUse.current = payload(columnOrder)
        }
        // set order locally
        setColumnOrder(columnOrderToUse.current)
        // set order in the external callback
        onColumnOrderChange(columnOrderToUse.current)
    }

    const onGroupingChange = (groupingUpdater) => {
        // call updater function to get new grouping values
        const newGroupingColumns = groupingUpdater(groupingColumns)

        // if we are entering a grouping state, we need to hide the row numbers
        if (newGroupingColumns?.length) {
            // adjust hiding index column on grouping change
            setColumnVisibility({
                [ROW_NUMBERS_DISPLAY_COLUMN_ID]: false,
            })
            // restore column order to match hidden mrt-row-numbers
            setColumnOrder([
                ROW_EXPAND_DISPLAY_COLUMN_ID,
                ...columnOrder.filter((id) => id !== ROW_NUMBERS_DISPLAY_COLUMN_ID),
            ])
            // remove default pinning state, pin grouping columns and expand controls
            setColumnPinning({
                left: [...newGroupingColumns, ROW_EXPAND_DISPLAY_COLUMN_ID],
            })
        } else {
            // otherwise, return to the default row number state and refresh the order
            setColumnVisibility({
                [ROW_NUMBERS_DISPLAY_COLUMN_ID]: true,
            })
            setColumnOrder([
                ...(enableRowNumbers ? [ROW_NUMBERS_DISPLAY_COLUMN_ID] : []),
                ...columnOrder.filter(
                    (id) => ![ROW_NUMBERS_DISPLAY_COLUMN_ID, ROW_EXPAND_DISPLAY_COLUMN_ID].includes(id),
                ),
            ])
            // reset to default pinning state
            setColumnPinning({
                ...(enableRowNumbers ? [ROW_NUMBERS_DISPLAY_COLUMN_ID] : []),
                ...columns.filter((column) => column.pinned).map((column) => String(column.id)),
            })
        }

        setGroupingColumns(newGroupingColumns)
    }

    useEffect(() => {
        if (!groupingColumns.length && !columnOrderToUse.current) {
            setColumnOrder([
                ...(enableRowNumbers ? [ROW_NUMBERS_DISPLAY_COLUMN_ID] : []),
                ...columns.map((column) => String(column.id)),
            ])
            setColumnPinning({
                left: [
                    ...(enableRowNumbers ? [ROW_NUMBERS_DISPLAY_COLUMN_ID] : []),
                    ...columns.filter((column) => column.pinned).map((column) => String(column.id)),
                ],
                right: [],
            })
        }
    }, [enableRowNumbers, columns, groupingColumns, columnOrderToUse])

    /*
        Basically, if a user scrolls it should hide the dropdown and unfocus all inputs to prevent
        any weird styles from happening with the dropdown. This is specific to the subheader filters
        provided by mantine and the column actions menu, which cause a lot of style issues when used with our specific styles.
    */
    useEffect(() => {
        const handleScroll = () => {
            // Unfocus all input elements
            const inputElements = document.querySelectorAll("input")
            inputElements.forEach((input) => input.blur())

            // Remove or modify the dropdown elements
            const dropdownElement = document.querySelector(".mantineColumnActionMenu")
            if (dropdownElement) {
                dropdownElement.remove()
            }

            // If we don't do this, there's a tiny little square of padding only that's left behind
            const mantineDropdownDiv = document.querySelector(".mantine-Menu-dropdown")

            if (mantineDropdownDiv) {
                // @ts-expect-error
                mantineDropdownDiv.style.padding = "0"
            }
        }

        const tableContainer = document.getElementsByClassName("mantine-Table-root")[0]

        if (tableContainer) {
            const tableDivThatScrolls = tableContainer.closest("div")

            if (tableDivThatScrolls) {
                tableDivThatScrolls.addEventListener("scroll", handleScroll)

                return () => {
                    tableDivThatScrolls.removeEventListener("scroll", handleScroll)
                }
            }
        }
    }, [])

    useEffect(() => {
        const allDragBtns = document.querySelectorAll("svg.tabler-icon-grip-horizontal")
        const allSortBtns = document.querySelectorAll("svg.tabler-icon-arrows-sort")
        const allColumnActionsBtns = document.querySelectorAll("svg.tabler-icon-dots-vertical")

        setPlumId(allDragBtns, "column-drag-drop")
        setPlumId(allSortBtns, "column-sort")
        setPlumId(allColumnActionsBtns, "column-open-action-menu")
    }, [])

    const [columnFilters, setColumnFilters] = useState([])
    const [sorting, setSorting] = useState([])

    const handleColumnFiltersChange = (payload) => {
        const columnFiltersToUse = payload(columnFilters)
        setColumnFilters(columnFiltersToUse)
        onColumnFiltersChange(columnFiltersToUse)
    }

    const handleSortingChange = (payload) => {
        const columnSortToUse = payload(sorting)
        setSorting(columnSortToUse)
        onSortingChange(columnSortToUse)
    }

    // Wrapping virtualization in a feature flag so we can develop iteratively here
    const virtualizationProps = {
        // virtualization props
        enableRowVirtualization: true,
        rowVirtualizerProps: {
            overscan: 20,
            measureElement: (el) => {
                // reach out to cell wrapper(s) in the row for actual content height
                // we shrink the actual row to 1 height for styling purposes
                const rowCellElements = el.querySelectorAll(".q-cell-wrapper")

                let maxHeight = -1
                // NodeList class does not have map/reduce functions, so this is the most performant solution
                rowCellElements.forEach((el) => (maxHeight = Math.max(maxHeight, el.getBoundingClientRect().height)))
                return maxHeight
            },
        },
    }

    const table = useMantineReactTable({
        columnResizeMode: "onChange",
        columns,
        data,
        enableColumnDragging,
        enableColumnOrdering,
        enableColumnResizing,
        enableDensityToggle,
        enableFilters: !isLoading,
        enableFullScreenToggle: enableFullScreen,
        enableGlobalFilter,
        enableGrouping,
        enablePagination: false,
        enableSorting: !isLoading,
        enableStickyHeader,
        enableStickyFooter,
        enableBottomToolbar: false,
        columnFilterDisplayMode: "subheader",
        manualSorting: !canFitInMemory,
        manualFiltering: !canFitInMemory,
        onSortingChange: handleSortingChange,
        onColumnFiltersChange: handleColumnFiltersChange,
        //
        filterFns: {
            ...filterFns,
            globalFilterFn,
        },
        // we cannot pass the filter function in directly, here, so name it identically to the prop and spread it
        // in filter functions above
        globalFilterFn: "globalFilterFn",
        aggregationFns,
        getSortedRowModel: getSortedRowModel(),
        /*
            by default, MRT will disable global filter for a column if the first row results in an undefined entry???
            so we provide this function to override this behavior and defer to column definitions since our model of a
            table allows undefined values
        */
        getColumnCanGlobalFilter,
        initialState: {
            expanded: true,
        },
        onColumnOrderChange: handleColumnOrderChange,
        positionToolbarAlertBanner: "head-overlay",
        positionToolbarDropZone: "none",
        onColumnSizingChange: handleColumnSizeChange,
        onGroupingChange,
        renderTopToolbarCustomActions,
        renderToolbarInternalActions,
        renderTopToolbar,
        rowCount,
        rowNumberMode: "original",
        state: {
            columnOrder,
            grouping: groupingColumns,
            columnPinning,
            columnFilters,
            sorting,
            columnVisibility,
        },
        ...virtualizationProps,

        // Styling
        mantinePaperProps: { className: classes.paper },
        mantineTableBodyCellProps: ({ column }) => {
            if (column.id === "-1") {
                // Sheets datum column index
                return { className: cx(classes.cell, classes.datumCell) }
            }
            return { className: classes.cell }
        },
        mantineTableBodyRowProps: { className: classes.row },
        mantineTableContainerProps: {
            className: classes.container,
            ref: tableContainerRef,
        },
        mantineTableHeadCellProps: ({ column }) => {
            const columnSize = column.getSize()
            const isFiltered = isColumnActivelyFiltered(column)
            const isSorted = column.getIsSorted()
            const finalClasses = [classes.header]
            const useSmallHeaderMode = columnSize < 144

            // in Small-display mode, never display filter or sort icons in the header
            if (useSmallHeaderMode) {
                finalClasses.push(classes.headerSmallDisplay)
                return { className: cx(...finalClasses) }
            }
            // otherwise, defer to sorted and filtered state to generate classes
            if (!isFiltered) {
                finalClasses.push(classes.headerHideFilterHover)
            }
            if (!isSorted) {
                finalClasses.push(classes.headerHideSortHover)
            }
            return { className: cx(...finalClasses) }
        },
        mantineTopToolbarProps: { className: classes.topToolbar },
        mantineTableFooterProps: { className: classes.footer },
        mantineTableHeadRowProps: { className: classes.headerRow },
        mantineToolbarAlertBannerProps: { className: classes.alertBanner },
        // this is specifically to hide the "#" in the number row column header,
        displayColumnDefOptions: {
            "mrt-row-numbers": {
                mantineTableHeadCellProps: {
                    sx: {
                        "&& div": {
                            display: "none",
                        },
                    },
                },
            },
        },
    })

    return <MantineReactTable table={table} />
}
